import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import CustomerSelect from '../../common/CustomerSelect';
import ReactDOM from 'react-dom';
import debounce from 'lodash/debounce';
import Layout from '../../layout/Layout';
import { Link, useSearchParams } from 'react-router-dom';
import { 
  getCartonConstraints, 
  calculateM2Rounding, 
  formatCartonInfo,
  CartonConstraint,
  M2RoundingResult 
} from '../../../utils/cartonRounding';

import quoteService, { QuoteCreate, QuoteItemCreate } from '../../../services/quoteService';
import inventoryService, { Inventory } from '../../../services/inventoryService';
import storeTypeService, { StoreType as StoreTypeModel } from '../../../services/storeTypeService';
import SkuImage from '../../common/SkuImage';
import customerService from '../../../services/customerService';
import supplierService from '../../../services/supplierService';
import { useStoreFilter } from '../../../hooks/useStoreFilter';
import { useAuth } from '../../../context/AuthContext';
import { useToast } from '../../../hooks/useToast';
import { isAdmin, isManager } from '../../../utils/roleUtils';
import { Toast } from '../../common/Toast';
import ExitConfirmationDialog from '../../common/ExitConfirmationDialog';
import ShippingConfirmationDialog from '../../common/ShippingConfirmationDialog';
import { useUnsavedChanges } from '../../../context/UnsavedChangesContext';
import invoicesNotesIcon from '../../../assets/invoicesNotesIcon.png';

const PageContainer = styled.div`
  padding: 0 32px 32px 32px;
  min-height: 100vh;
  overflow-y: auto;
`;

// Updated Back Button without text and aligned with breadcrumb
const BackButton = styled.button`
  display: flex;
  align-items: center;
  border: none;
  background: none;
  color: #042B41;
  cursor: pointer;
  padding: 0;
  margin-right: 10px;
  
  svg {
    width: 18px;
    height: 18px;
  }

  &:hover {
    color: #031f30;
  }
`;

const BreadcrumbContainer = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 24px;
`;

const BreadcrumbNav = styled.div`
  display: flex;
  align-items: center;
  font-size: 24px;
  font-weight: 600;
  color: #042B41;
`;

const BreadcrumbSeparator = styled.span`
  margin: 0 10px;
  color: #6B7280;
`;

const QuotesText = styled(Link)`
  color: #6B7280;
  text-decoration: none;
  font-weight: 600;
  
  &:hover {
    text-decoration: underline;
  }
`;

const CreateQuoteTitle = styled.span`
  font-weight: 700;
  color: #111827;
`;

const FormContainer = styled.form`
  width: 100%;
  border: 1px solid #E5E7EB;
  border-radius: 0 0 4px 4px;
  padding: 1.5rem;
  background-color: white;
  margin-top: 0;
  overflow: visible;
  position: relative;
  margin-bottom: 1rem;
`;

// Updated to make the form layout more consistent
const FormRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  margin-bottom: 0.75rem;
  width: 100%;
`;

const FormColumn = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const FormLabel = styled.label`
  font-size: 0.875rem;
  font-weight: 500;
  color: #111827;
  margin-bottom: 0.5rem;
  
  .required {
    color: #EF4444;
    margin-left: 4px;
  }
`;

const FormInput = styled.input`
  padding: 0.625rem;
  border: 1px solid #D1D5DB;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  height: 38px;
  width: 100%;
  
  &:focus {
    outline: none;
    border-color: #042B41;
    box-shadow: 0 0 0 2px rgba(4, 43, 65, 0.1);
  }
`;

const FormSelect = styled.select`
  padding: 0.625rem;
  border: 1px solid #D1D5DB;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  background-color: white;
  height: 38px;
  width: 100%;
  appearance: none;
  padding-right: 2rem;
  
  &:focus {
    outline: none;
    border-color: #042B41;
    box-shadow: 0 0 0 2px rgba(4, 43, 65, 0.1);
  }
`;

const FormTextarea = styled.textarea<{ disabled?: boolean }>`
  padding: 0.625rem;
  border: 1px solid #D1D5DB;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  min-height: 38px;
  resize: vertical;
  width: 100%;
  background-color: ${props => props.disabled ? '#f3f4f6' : 'white'};
  color: ${props => props.disabled ? '#9ca3af' : '#111827'};
  cursor: ${props => props.disabled ? 'not-allowed' : 'text'};
  
  &:focus {
    outline: none;
    border-color: ${props => props.disabled ? '#D1D5DB' : '#042B41'};
    box-shadow: ${props => props.disabled ? 'none' : '0 0 0 2px rgba(4, 43, 65, 0.1)'};
  }
`;



const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 1.5rem;
  border: 1px solid #E5E7EB;
  background-color: #fff;
  position: relative;
  z-index: 1;
`;

const TableHeader = styled.thead`
  background-color: #042B41;
  color: white;
  
  th {
    padding: 0.75rem;
    text-align: left;
    font-weight: 500;
    font-size: 0.875rem;
    border: 0.5px solid white;
  }
`;

const TableBody = styled.tbody`
  tr {
    border-bottom: 1px solid #E5E7EB;
    
    &:last-child {
      border-bottom: none;
    }
  }
  
  td {
    padding: 0.75rem;
    font-size: 0.875rem;
    border: 1px solid #E5E7EB;
    position: relative;
    overflow: visible;
    vertical-align: top;
    
    &.wrap {
      white-space: normal;
      word-break: break-word;
      overflow: visible;
    }
  }
`;

// Add helper function to determine which quantity field is active
const getActiveQuantityField = (quantity: QuoteItemData['quantity'], item?: QuoteItemData): string | null => {
  if (quantity.units && quantity.units > 0) return 'units';
  // Don't treat boxes as active if they were set programmatically from M2 rounding
  if (quantity.boxes && quantity.boxes > 0 && !(item as any)?.boxesFromM2Rounding) return 'boxes';
  if (quantity.pieces && quantity.pieces > 0) return 'pieces';
  
  // Check if BLM is active first since it should override M2
  // Only consider BLM active if it has a meaningful value AND M2 is calculated
  if (quantity.blm && quantity.blm > 0 && quantity.m2 && quantity.m2 > 0) return 'blm';
  
  // Only consider M2 active if BLM is not active
  if (quantity.m2 && quantity.m2 > 0 && (!quantity.blm || quantity.blm === 0)) return 'm2';
  
  return null;
};

// Update the QuantityInput styled component to handle disabled state
const QuantityInput = styled(FormInput)<{ isDisabled?: boolean }>`
  width: 60px;
  text-align: center;
  padding: 0.5rem;
  border: 1px solid #D1D5DB;
  border-radius: 4px;
  height: 36px;
  font-size: 14px;
  background-color: ${props => props.isDisabled ? '#f3f4f6' : 'white'};
  color: ${props => props.isDisabled ? '#9ca3af' : '#111827'};
  cursor: ${props => props.isDisabled ? 'not-allowed' : 'text'};
  
  &:focus {
    outline: none;
    border-color: ${props => props.isDisabled ? '#D1D5DB' : '#042B41'};
    box-shadow: ${props => props.isDisabled ? 'none' : '0 0 0 2px rgba(4, 43, 65, 0.1)'};
  }
`;

const AddButton = styled.button<{ disabled?: boolean }>`
  background-color: ${props => props.disabled ? '#D1D5DB' : '#042B41'};
  color: white;
  border: none;
  width: 28px;
  height: 28px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  font-size: 18px;
  opacity: ${props => props.disabled ? 0.6 : 1};
  
  &:hover {
    background-color: ${props => props.disabled ? '#D1D5DB' : '#031F30'};
  }
`;

const RemoveButton = styled.button<{ disabled?: boolean }>`
  background-color: white;
  color: #EF4444;
  border: 1px solid #EF4444;
  width: 28px;
  height: 28px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  font-size: 18px;
  opacity: ${props => props.disabled ? 0.5 : 1};
  
  &:hover {
    background-color: ${props => props.disabled ? 'white' : '#FEF2F2'};
  }
`;

const NotesIcon = styled.span<{ hasNotes?: boolean }>`
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 18px;
    height: 18px;
    object-fit: contain;
    filter: ${props => props.hasNotes ? 'invert(15%) sepia(95%) saturate(6932%) hue-rotate(358deg) brightness(95%) contrast(112%)' : 'none'};
  }
`;

const NotesModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const NotesModalContent = styled.div`
  background-color: white;
  border-radius: 8px;
  padding: 24px;
  width: 90%;
  max-width: 500px;
  max-height: 80vh;
  overflow-y: auto;
`;

const NotesModalTitle = styled.h3`
  margin: 0 0 16px 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: #111827;
`;

const NotesModalTextarea = styled.textarea`
  width: 100%;
  min-height: 150px;
  padding: 12px;
  border: 1px solid #D1D5DB;
  border-radius: 4px;
  font-size: 0.875rem;
  resize: vertical;
  background-color: #F9FAFB;
  color: #111827;
  cursor: default;
  
  &:focus {
    outline: none;
    border-color: #042B41;
  }
`;

const NotesButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 16px;
`;

const NotesButton = styled.button`
  padding: 8px 16px;
  border-radius: 4px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  border: none;
  background-color: #042B41;
  color: white;

  &:hover {
    background-color: #031F30;
  }
`;

const CloseNotesButton = styled(NotesButton)`
  background-color: white;
  color: #111827;
  border: 1px solid #D1D5DB;

  &:hover {
    background-color: #F9FAFB;
  }
`;

const TableContainer = styled.div`
  width: 100%;
  overflow-x: auto;
  overflow-y: visible;
  margin-top: 1.5rem;
  margin-bottom: 1rem;
  position: relative;
  z-index: 1;
  min-height: 200px;
`;

const TotalSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  margin-top: 1rem;
`;

const TotalRow = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-bottom: 0.75rem;
  
  &:last-child {
    margin-bottom: 0;
    margin-top: 0.5rem;
  }
`;

const TotalLabel = styled.span`
  font-size: 0.875rem;
  font-weight: 500;
  width: 180px;
  text-align: right;
  margin-right: 1rem;
  display: flex;
  justify-content: flex-end;
  align-items: center;
`;



// Add styled input for total values
const TotalInput = styled.input`
  font-size: 0.875rem;
  font-weight: 500;
  width: 80px;
  text-align: right;
  padding: 4px 8px;
  border: 1px solid #D1D5DB;
  border-radius: 4px;
`;

// Add currency symbol component
const CurrencySymbol = styled.span`
  font-size: 0.875rem;
  font-weight: 500;
  margin-right: 4px;
`;

const Divider = styled.hr`
  width: 280px;
  margin: 0.5rem 0;
  border: none;
  border-top: 1px solid #E5E7EB;
  align-self: flex-end;
`;

const GrandTotalLabel = styled(TotalLabel)`
  font-weight: 600;
`;

// Non-editable but visually consistent input for grand total
const GrandTotalInput = styled(TotalInput)`
  font-weight: 600;
  background-color: white;
`;

const ButtonsContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin-top: 2rem;
`;

const CancelButton = styled.button`
  padding: 0.75rem 2rem;
  border: 1px solid #D1D5DB;
  border-radius: 0.375rem;
  background-color: white;
  color: #111827;
  font-weight: 500;
  font-size: 0.875rem;
  cursor: pointer;
  width: 200px;
  height: 48px;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: #F9FAFB;
  }
`;

const SubmitButton = styled.button`
  padding: 0.75rem 2rem;
  border: none;
  border-radius: 0.375rem;
  background-color: #042B41;
  color: white;
  font-weight: 500;
  font-size: 0.875rem;
  cursor: pointer;
  width: 200px;
  height: 48px;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: #031F30;
  }
`;

const SaveAsDraftButton = styled(CancelButton)``;

// Create a special notes textarea component that spans the full height of two rows
const NotesTextarea = styled(FormTextarea)`
  height: 38px;
  min-height: 38px;
  resize: vertical;
`;

// Update the customer select wrapper
const CustomerSelectWrapper = styled.div`
  width: 100%;
  
  input {
    height: 38px !important;
    font-size: 0.875rem;
    padding: 0.625rem;
  }
`;



// Create a DateInput component with the right styling
const DateInput = styled(FormInput)`
  width: 100%;
  cursor: pointer;
  position: relative;
  
  &::-webkit-calendar-picker-indicator {
    opacity: 0;
    cursor: pointer;
    position: absolute;
    right: 0;
    top: 0;
    width: 100%;
    height: 100%;
    margin: 0;
    padding: 0;
  }
  
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    display: none;
  }
  
  /* Additional styling for better calendar positioning */
  &::-webkit-datetime-edit {
    width: 100%;
    padding: 0;
  }
  
  &::-webkit-datetime-edit-fields-wrapper {
    width: 100%;
  }
`;

// Styled components for customer pickup checkbox
const DeliverToAddressContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
`;

const DeliverToAddressInputWrapper = styled.div`
  flex: 1;
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

const CheckboxInput = styled.input`
  width: 16px;
  height: 16px;
  cursor: pointer;
`;

const CheckboxLabel = styled.label`
  font-size: 0.875rem;
  color: #111827;
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
`;

const DeliverToAddressInput = styled(FormInput)<{ disabled?: boolean }>`
  background-color: ${props => props.disabled ? '#F9FAFB' : 'white'};
  color: ${props => props.disabled ? '#6B7280' : '#111827'};
  cursor: ${props => props.disabled ? 'not-allowed' : 'text'};
`;



interface QuoteFormData {
  storeTypeId: string;
  customerId: string; // Always store as string for compatibility with UI components
  companyId: string;
  companyName: string; // Keep for UI display but not sent to API
  deliverToAddress: string;
  date: string;
  notes: string;
}

interface QuoteItemData {
  id: string;
  sku: string;
  description: string;
  quantity: {
    units: number | null;
    boxes: number | null;
    pieces: number | null;
    m2: number | null;
    blm: number | null;
    total: number;
  };
  unitPrice: number;
  totalPrice: number;
  cartonConstraint?: CartonConstraint;
  m2RoundingResult?: M2RoundingResult;
  boxesFromM2Rounding?: boolean; // Track if boxes were set programmatically
  notes?: string;
}





// Add new styled components for unit price and total price
const UnitPriceInput = styled(FormInput)`
  width: 100%;
  max-width: 130px;
  text-align: right;
  padding-right: 8px;
  font-size: 0.875rem;
  white-space: nowrap;
  overflow: visible;
  box-sizing: border-box;
  
  &:read-only {
    background-color: #f9fafb;
    color: #111827; /* Darker text color for better readability */
  }
`;

const TotalPriceInput = styled(FormInput)`
  width: 100%;
  max-width: 140px;
  text-align: right;
  background-color: #f9fafb;
  padding-right: 8px;
  font-size: 0.875rem;
  box-sizing: border-box;
  cursor: not-allowed;
  color: #6B7280;
  
  &:focus {
    outline: none;
    box-shadow: none;
  }
`;

// Add new SearchableSkuDropdown component
const SearchableSkuDropdownContainer = styled.div`
  position: relative;
  width: 100%;
`;

const SearchInput = styled.textarea<{ disabled?: boolean }>`
  padding: 0.625rem;
  border: 1px solid #D1D5DB;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  min-height: 48px; /* show at least two lines */
  width: 100%;
  background-color: ${props => props.disabled ? '#f3f4f6' : 'white'};
  color: ${props => props.disabled ? '#9ca3af' : '#111827'};
  resize: vertical; /* allow expanding top/bottom */
  line-height: 1.25rem;
  overflow: auto;
  cursor: ${props => props.disabled ? 'not-allowed' : 'text'};
  
  &:focus {
    outline: none;
    border-color: ${props => props.disabled ? '#D1D5DB' : '#042B41'};
    box-shadow: ${props => props.disabled ? 'none' : '0 0 0 2px rgba(4, 43, 65, 0.1)'};
  }
`;

const DropdownList = styled.div<{ isOpen: boolean }>`
  position: fixed;
  background: white;
  border: 1px solid #D1D5DB;
  border-radius: 0.375rem;
  max-height: 300px;
  overflow-y: auto;
  z-index: 9999;
  display: ${props => props.isOpen ? 'block' : 'none'};
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  min-width: 200px;
`;

const DropdownItem = styled.div<{ isHighlighted: boolean }>`
  padding: 0.375rem;
  cursor: pointer;
  font-size: 0.875rem;
  background-color: ${props => props.isHighlighted ? '#F3F4F6' : 'white'};
  border-bottom: 1px solid #E5E7EB;
  display: flex;
  align-items: center;
  gap: 8px;
  
  &:hover {
    background-color: #F3F4F6;
  }
  
  &:last-child {
    border-bottom: none;
  }
`;

const DropdownItemContent = styled.div`
  flex: 1;
  pointer-events: none; /* Prevent event conflicts with image clicks */
`;

const SelectedItemContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
`;

const NoResultsItem = styled.div`
  padding: 0.625rem;
  font-size: 0.875rem;
  color: #6B7280;
  text-align: center;
  font-style: italic;
`;

interface SearchableSkuDropdownProps {
  value: string;
  onChange: (value: string) => void;
  inventoryItems: any[];
  disabled?: boolean;
  placeholder?: string;
  lastInventoryRefresh?: number;
  title?: string;
}

const SearchableSkuDropdown: React.FC<SearchableSkuDropdownProps> = ({
  value,
  onChange,
  inventoryItems,
  disabled = false,
  placeholder = "Search SKU",
  lastInventoryRefresh,
  title
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Use debounced search function for backend search
  const debouncedSearchSKUs = useMemo(
    () => debounce(async (term: string) => {
      if (!term.trim()) {
        setSearchResults([]);
        setIsSearching(false);
        return;
      }

      setIsSearching(true);
      try {
        const results = await inventoryService.searchSKUs(term, 10);
        setSearchResults(results);
      } catch (error) {
        console.error('Error searching SKUs:', error);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 300),
    []
  );

  // Calculate dropdown position
  const updateDropdownPosition = () => {
    if (inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY + 2,
        left: rect.left + window.scrollX,
        width: rect.width
      });
    }
  };

  // Trigger backend search when search term changes
  useEffect(() => {
    if (isOpen && searchTerm.trim()) {
      debouncedSearchSKUs(searchTerm);
    }
  }, [searchTerm, isOpen, debouncedSearchSKUs]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      
      // Check if the click is outside both the container and the dropdown
      const isOutsideContainer = containerRef.current && !containerRef.current.contains(target);
      const isOutsideDropdown = dropdownRef.current && !dropdownRef.current.contains(target);
      
      if (isOutsideContainer && isOutsideDropdown) {
        setIsOpen(false);
        setHighlightedIndex(-1);
      }
    };

    const handleScroll = () => {
      if (isOpen) {
        updateDropdownPosition();
      }
    };

    const handleResize = () => {
      if (isOpen) {
        updateDropdownPosition();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleResize);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleResize);
    };
  }, [isOpen]);

  // Update search term when value changes externally
  useEffect(() => {
    // Always sync searchTerm with value, including when value is cleared
    if (value !== searchTerm) {
      setSearchTerm(value || '');
    }
  }, [value]);

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    setSearchTerm(newValue);
    setIsOpen(true);
    setHighlightedIndex(-1);
    updateDropdownPosition();

    // If the input is cleared, also clear the selected value
    if (!newValue.trim()) {
      onChange('');
    }
  };

  const handleInputFocus = () => {
    updateDropdownPosition();
    setIsOpen(true);
  };

  const handleBlur = () => {
    // If the search term doesn't match the currently selected value, 
    // it's an invalid input (user typed something but didn't select)
    if (searchTerm !== value) {
      // Revert search term to the last valid value
      setSearchTerm(value || '');
    }
    // Small delay to allow click events on dropdown items to fire first
    setTimeout(() => {
      setIsOpen(false);
      setHighlightedIndex(-1);
    }, 200);
  };

  const handleItemSelect = (item: any) => {
    console.log('SearchableSkuDropdown - Item selected:', item?.sku_code);
    
    if (!item || !item.sku_code) {
      console.error('SearchableSkuDropdown - Invalid item selected:', item);
      return;
    }

    setSearchTerm(item.sku_code);
    onChange(item.sku_code);
    setIsOpen(false);
    setHighlightedIndex(-1);
  };

  // Enhanced click handler with better event management
  const handleItemClick = (e: React.MouseEvent, item: any) => {
    e.preventDefault();
    e.stopPropagation();
    console.log('SearchableSkuDropdown - Item clicked:', item?.sku_code);
    handleItemSelect(item);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        updateDropdownPosition();
        setIsOpen(true);
        return;
      }
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev =>
          prev < searchResults.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && searchResults[highlightedIndex]) {
          handleItemSelect(searchResults[highlightedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setHighlightedIndex(-1);
        inputRef.current?.blur();
        break;
    }
  };

  // Check if current value has a selected item
  const selectedItem = value ? (
    searchResults.find(item => item.sku_code === value) || 
    inventoryItems.find(item => item.sku_code === value)
  ) : null;

  // Create portal element for dropdown
  const dropdownPortal = isOpen && !disabled ? ReactDOM.createPortal(
    <DropdownList 
      ref={dropdownRef}
      isOpen={isOpen}
      style={{
        top: dropdownPosition.top,
        left: dropdownPosition.left,
        width: dropdownPosition.width
      }}
    >
      {isSearching ? (
        <NoResultsItem>
          Searching SKUs...
        </NoResultsItem>
      ) : searchResults.length > 0 ? (
        searchResults.map((item, index) => (
          <DropdownItem
            key={`${item.id}-${index}`}
            isHighlighted={index === highlightedIndex}
            onClick={(e) => handleItemClick(e, item)}
            onMouseDown={(e) => e.preventDefault()} // Prevent input blur
          >
            <SkuImage
              imagePath={item.image_path}
              alt="SKU"
              width={40}
              height={40}
              enableModal={false} // Disable modal in dropdown to prevent conflicts
              showBlankWhenNoImage={true}
              key={`${item.id}-${lastInventoryRefresh}`}
            />
            <DropdownItemContent>
              <div style={{ fontWeight: '500' }}>{item.sku_code}</div>
              {item.description && (
                <div style={{ fontSize: '0.75rem', color: '#6B7280', marginTop: '2px' }}>
                  {item.description}
                </div>
              )}
            </DropdownItemContent>
          </DropdownItem>
        ))
      ) : searchTerm.trim() ? (
        <NoResultsItem>
          No SKUs found matching "{searchTerm}"
        </NoResultsItem>
      ) : (
        <NoResultsItem>
          Type to search for SKUs
        </NoResultsItem>
      )}
    </DropdownList>,
    document.body
  ) : null;

  return (
    <>
      <SearchableSkuDropdownContainer ref={containerRef} title={title}>
        {selectedItem ? (
          <SelectedItemContainer>
            <SkuImage
              imagePath={selectedItem?.image_path}
              alt="SKU"
              width={32}
              height={32}
              enableModal={true}
              showBlankWhenNoImage={true}
              key={`${selectedItem?.id}-${lastInventoryRefresh}`}
            />
            <SearchInput
              ref={inputRef}
              value={searchTerm}
              onChange={handleInputChange}
              onFocus={handleInputFocus}
              onBlur={handleBlur}
              onKeyDown={handleKeyDown}
              placeholder={disabled ? 'Loading SKUs' : placeholder}
              disabled={disabled}
              title={title}
              style={{ flex: 1 }}
              rows={2}
            />
          </SelectedItemContainer>
        ) : (
          <SearchInput
            ref={inputRef}
            value={searchTerm}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            placeholder={disabled ? 'Loading SKUs' : placeholder}
            disabled={disabled}
            title={title}
            rows={2}
          />
        )}
      </SearchableSkuDropdownContainer>
      
      {dropdownPortal}
    </>
  );
};

// Add SelectWrapper for custom dropdown icon like EditInvoiceForm
const SelectWrapper = styled.div`
  position: relative;

  &::after {
    content: '';
    position: absolute;
    top: 50%;
    right: 12px;
    transform: translateY(-50%);
    width: 0;
    height: 0;
    border-left: 5px solid transparent;
    border-right: 5px solid transparent;
    border-top: 5px solid #333;
    pointer-events: none;
  }
`;


// Add styled components for shipping modal (same as EditInvoiceForm)
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 8px;
  padding: 24px;
  max-width: 700px;
  width: 95%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 12px;
  border-bottom: 1px solid #E5E7EB;
`;

const ModalTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: #111827;
  margin: 0;
`;

const ModalCloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #6B7280;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    color: #111827;
  }
`;

const ModalBody = styled.div`
  color: #374151;
  line-height: 1.6;
  
  h3 {
    color: #111827;
    font-weight: 600;
    margin: 16px 0 8px 0;
    
    &:first-child {
      margin-top: 0;
    }
  }
  
  p {
    margin: 8px 0;
  }
  
  ul {
    margin: 8px 0;
    padding-left: 20px;
  }
  
  li {
    margin: 4px 0;
  }
`;

// Update the InfoIcon to be clickable (same as EditInvoiceForm)
const ClickableInfoIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background-color: #3B82F6;
  color: white;
  font-size: 12px;
  font-weight: bold;
  cursor: pointer;
  margin-right: 8px;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: #2563EB;
  }
`;

// Small info icon for carton info in total column
const SmallInfoIcon = styled.span<{ showTooltip: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background-color: #F59E0B;
  color: white;
  font-size: 10px;
  font-weight: bold;
  cursor: pointer;
  margin-left: 4px;
  transition: background-color 0.2s;
  position: relative;
  
  &:hover {
    background-color: #D97706;
  }
`;

// Carton info tooltip (simple tooltip, not modal)
const CartonInfoTooltip = styled.div<{ show: boolean }>`
  position: absolute;
  top: -45px;
  right: -150px;
  width: 280px;
  background-color: #FEF3C7;
  border: 1px solid #F59E0B;
  border-radius: 4px;
  padding: 4px 6px;
  font-size: 11px;
  color: #D97706;
  line-height: 1.3;
  z-index: 1000;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  visibility: ${props => props.show ? 'visible' : 'hidden'};
  opacity: ${props => props.show ? 1 : 0};
  transition: opacity 0.2s, visibility 0.2s;
  pointer-events: none;
`;

const CreateQuotePage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editQuoteId = searchParams.get('edit');
  const returnTo = searchParams.get('returnTo');
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const storeFilter = useStoreFilter();
  const { user } = useAuth();
  const toast = useToast();
  const canEditPrice = isAdmin(user) || isManager(user);
  const { setHasUnsavedChanges } = useUnsavedChanges();

  // Add customer pickup state
  const [customerPickup, setCustomerPickup] = useState(false);

  // Add state for carton info tooltip
  const [cartonInfoTooltip, setCartonInfoTooltip] = useState<{
    visible: boolean;
    itemIndex: number | null;
  }>({ visible: false, itemIndex: null });

  const [formData, setFormData] = useState<QuoteFormData>({
    storeTypeId: storeFilter.isStoreRestricted && storeFilter.userStoreTypeId ? String(storeFilter.userStoreTypeId) : '', // Auto-set for restricted users
    customerId: '',  // Start with empty string
    companyId: 'c6c6b678-dc5c-4693-9a6e-d22e1b97dd05', // Default company ID
    companyName: '',
    deliverToAddress: '',
    date: new Date().toISOString().split('T')[0],
    notes: ''
  });

  const [items, setItems] = useState<QuoteItemData[]>([
    {
      id: '1',
      sku: '',
      description: '',
      quantity: {
        units: null,
        boxes: null,
        pieces: null,
        m2: null,
        blm: null,
        total: 0
      },
      unitPrice: 0,
      totalPrice: 0
    }
  ]);

  const [totals, setTotals] = useState({
    orderTotal: 0,
    priceExclGst: 0,
    gst: 0,
    shipping: 0,
    grandTotal: 0
  });

  const [inventoryItems, setInventoryItems] = useState<Inventory[]>([]);

  // Add state for store types and loading indicators
  const [storeTypes, setStoreTypes] = useState<StoreTypeModel[]>([]);
  const [isLoadingStoreTypes, setIsLoadingStoreTypes] = useState(false);
  const [isLoadingInventory, setIsLoadingInventory] = useState(false);
  const [lastInventoryRefresh, setLastInventoryRefresh] = useState<number>(Date.now());

  // Add state to store customer details with price list info
  const [selectedCustomer, setSelectedCustomer] = useState<any>(null);

  // Add state for shipping details modal
  const [isShippingModalOpen, setIsShippingModalOpen] = useState(false);

  // Add state for carton constraints and M2 rounding
  const [cartonConstraints, setCartonConstraints] = useState<{ [sku: string]: CartonConstraint }>({});

  // Add state for exit confirmation
  const [showExitConfirmation, setShowExitConfirmation] = useState(false);
  const [initialFormData, setInitialFormData] = useState<QuoteFormData | null>(null);
  const [initialItems, setInitialItems] = useState<QuoteItemData[]>([]);
  // Add state for shipping confirmation dialog
  const [showShippingConfirmation, setShowShippingConfirmation] = useState(false);
  const [pendingSubmit, setPendingSubmit] = useState<(() => void) | null>(null);
  
  // Add state for Notes modal
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [currentNotesItemId, setCurrentNotesItemId] = useState<string | null>(null);
  const [currentNotesValue, setCurrentNotesValue] = useState<string>('');
  
  // Use a ref to track if we've set the store type to avoid unnecessary updates
  const storeTypeSetRef = useRef<boolean>(false);

  // Initialize baseline data
  useEffect(() => {
    if (!initialFormData) {
      setInitialFormData(JSON.parse(JSON.stringify(formData)));
      setInitialItems(JSON.parse(JSON.stringify(items)));
    }
  }, []);

  // Function to check if form has changed
  const hasFormDataChanged = (): boolean => {
    if (!initialFormData) return false;

    // Compare form data
    if (
      formData.storeTypeId !== initialFormData.storeTypeId ||
      formData.customerId !== initialFormData.customerId ||
      formData.companyId !== initialFormData.companyId ||
      formData.companyName !== initialFormData.companyName ||
      formData.deliverToAddress !== initialFormData.deliverToAddress ||
      formData.date !== initialFormData.date ||
      formData.notes !== initialFormData.notes
    ) {
      return true;
    }

    // Compare items
    if (items.length !== initialItems.length) return true;

    for (let i = 0; i < items.length; i++) {
      const current = items[i];
      const initial = initialItems[i];
      if (
        current.sku !== initial.sku ||
        current.description !== initial.description ||
        current.unitPrice !== initial.unitPrice ||
        current.quantity.total !== initial.quantity.total ||
        current.quantity.units !== initial.quantity.units ||
        current.quantity.boxes !== initial.quantity.boxes ||
        current.quantity.pieces !== initial.quantity.pieces ||
        current.quantity.m2 !== initial.quantity.m2 ||
        current.quantity.blm !== initial.quantity.blm
      ) {
        return true;
      }
    }

    return false;
  };

  const handleCancel = () => {
    const hasChanges = hasFormDataChanged();
    
    // If initialFormData is null, check if form has any meaningful data
    if (!initialFormData) {
      const hasAnyData = 
        formData.storeTypeId !== '' ||
        formData.customerId !== '' ||
        formData.companyName !== '' ||
        formData.deliverToAddress !== '' ||
        formData.notes !== '' ||
        (items && items.length > 0 && items.some(item => item.sku !== ''));
      
      if (hasAnyData) {
        setShowExitConfirmation(true);
        return;
      }
    }
    
    if (hasChanges) {
      setShowExitConfirmation(true);
    } else {
      setHasUnsavedChanges(false);
      if (returnTo) {
        navigate(decodeURIComponent(returnTo));
      } else {
        navigate('/quotes');
      }
    }
  };

  const handleConfirmExit = () => {
    setShowExitConfirmation(false);
    setHasUnsavedChanges(false);
    if (returnTo) {
      navigate(decodeURIComponent(returnTo));
    } else {
      navigate('/quotes');
    }
  };

  const handleCancelExit = () => {
    setShowExitConfirmation(false);
  };

  // Update store type when storeFilter becomes available (user data loads)
  // This runs on mount and whenever user data becomes available
  useEffect(() => {
    // Update if user is store-restricted and store type is available
    // Also update if current storeTypeId is empty or doesn't match user's store type
    if (storeFilter.isStoreRestricted && storeFilter.userStoreTypeId) {
      setFormData(prev => {
        // Always update if the store type is empty or doesn't match
        const shouldUpdate = !prev.storeTypeId || 
                            prev.storeTypeId === '' || 
                            prev.storeTypeId !== String(storeFilter.userStoreTypeId);
        
        if (shouldUpdate) {
          console.log('✅ Updating quote store type from storeFilter:', storeFilter.userStoreTypeId, 'Current:', prev.storeTypeId);
          storeTypeSetRef.current = true;
          return {
            ...prev,
            storeTypeId: String(storeFilter.userStoreTypeId)
          };
        }
        return prev;
      });
    }
  }, [storeFilter.isStoreRestricted, storeFilter.userStoreTypeId, storeFilter.userStoreType, user?.store_type?.id]);
  
  // Additional effect to catch user data loading after component mount
  // This ensures we catch the update even if the above effect doesn't trigger
  useEffect(() => {
    if (!storeTypeSetRef.current && user?.store_type?.id && (user.role === 'staff' || user.role === 'manager')) {
      console.log('✅ Setting quote store type from user data (fallback):', user.store_type.id);
      setFormData(prev => {
        if (!prev.storeTypeId || prev.storeTypeId === '') {
          storeTypeSetRef.current = true;
          return {
            ...prev,
            storeTypeId: String(user.store_type!.id)
          };
        }
        return prev;
      });
    }
  }, [user?.store_type?.id, user?.role]);

  // Update context when form changes
  useEffect(() => {
    const hasChanges = hasFormDataChanged();
    setHasUnsavedChanges(hasChanges);
  }, [formData, items, initialFormData, setHasUnsavedChanges]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle customer pickup checkbox
  const handleCustomerPickupChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = e.target.checked;
    setCustomerPickup(isChecked);
    
    if (isChecked) {
      setFormData(prev => ({
        ...prev,
        deliverToAddress: 'Customer Pickup'
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        deliverToAddress: ''
      }));
    }
  };

  const handleCustomerSelect = (customerId: string, customerName: string) => {
    // Store as string in the form data
    setFormData(prev => ({
      ...prev,
      customerId,
      companyName: customerName
    }));

    // Fetch customer details to get price list information
    const fetchCustomerDetails = async () => {
      try {
        const customer = await customerService.getCustomerById(customerId);
        setSelectedCustomer(customer);
        console.log('Selected customer with price list:', customer);
      } catch (error) {
        console.error('Error fetching customer details:', error);
        setSelectedCustomer(null);
      }
    };

    fetchCustomerDetails();
  };

  // Clear items when customer is removed
  useEffect(() => {
    // If customerId becomes empty and there are items with SKUs, clear them
    if ((!formData.customerId || formData.customerId.trim() === '') && items.length > 0) {
      const hasItemsWithSku = items.some(item => item.sku && item.sku.trim() !== '');
      
      if (hasItemsWithSku) {
        // Clear all items or reset them to empty state
        setItems([{
          id: '1',
          sku: '',
          description: '',
          quantity: {
            units: null,
            boxes: null,
            pieces: null,
            m2: null,
            blm: null,
            total: 0
          },
          unitPrice: 0,
          totalPrice: 0
        }]);
        
        // Also clear selected customer
        setSelectedCustomer(null);
      }
    }
  }, [formData.customerId]);

  // Helper function to handle errors and show user-friendly messages
  const handleError = (error: any, context: string = 'operation') => {
    console.error(`Error in ${context}:`, error);
    
    let userMessage = '';
    
    if (error instanceof Error) {
      const errorMessage = error.message;
      
      // Handle specific validation errors
      if (errorMessage.includes('customer_id') && errorMessage.includes('Invalid UUID or integer format')) {
        userMessage = 'Please select a customer';
      } else if (errorMessage.includes('Please select a customer')) {
        userMessage = 'Please select a customer';
      } else if (errorMessage.includes('Please select a store type')) {
        userMessage = 'Please select a store type';
      } else if (errorMessage.includes('Please add at least one item')) {
        userMessage = 'Please add at least one item with SKU';
      } else if (errorMessage.includes('Validation errors')) {
        // Extract more specific validation errors
        if (errorMessage.includes('customer_id')) {
          userMessage = 'Please select a customer';
        } else if (errorMessage.includes('store_type_id')) {
          userMessage = 'Please select a store type';
        } else {
          userMessage = 'Please check your input and try again';
        }
      } else if (errorMessage.includes('Failed to save quote') || errorMessage.includes('Failed to create quote')) {
        userMessage = 'Failed to save quote. Please try again.';
      } else if (errorMessage.length > 0 && errorMessage.length < 100) {
        // Use the original message if it's short and likely user-friendly
        userMessage = errorMessage;
      } else {
        userMessage = `Failed to ${context}. Please try again.`;
      }
    } else {
      userMessage = `Failed to ${context}. Please try again.`;
    }
    
    // Show toast notification
    toast.showToast(userMessage, { type: 'error' });
    
    // Clear the error state to remove the red bar
    setError(null);
  };

  // Function to get price for SKU based on customer's price list
  const getPriceForCustomerPriceList = (inventoryItem: any, customerPriceListId?: number | null): number => {
    // If no customer or no price list, fall back to trade_price
    if (!customerPriceListId || !inventoryItem) {
      return inventoryItem?.trade_price || 0;
    }

    console.log(`Getting price for customer price list ID: ${customerPriceListId}`, { inventoryItem });

    // Check if this is supplier data with PriceList mapping
    if (inventoryItem.PriceList && typeof inventoryItem.PriceList === 'object') {
      const priceListKey = customerPriceListId.toString();
      const priceFromPriceList = inventoryItem.PriceList[priceListKey];
      
      console.log(`Supplier PriceList lookup for customer price list ID ${customerPriceListId}:`, {
        priceListKey,
        priceFromPriceList,
        availablePrices: inventoryItem.PriceList
      });
      
      if (priceFromPriceList && priceFromPriceList > 0) {
        console.log(`Using supplier price: $${priceFromPriceList} for price list ID ${customerPriceListId}`);
        return priceFromPriceList;
      }
    }
    
    // Check direct price_list field (could be in response format)
    if (inventoryItem.price_list && typeof inventoryItem.price_list === 'object') {
      const priceListKey = customerPriceListId.toString();
      const priceFromPriceList = inventoryItem.price_list[priceListKey];
      
      console.log(`Direct price_list lookup for customer price list ID ${customerPriceListId}:`, {
        priceListKey,
        priceFromPriceList,
        availablePrices: inventoryItem.price_list
      });
      
      if (priceFromPriceList && priceFromPriceList > 0) {
        console.log(`Using direct price_list price: $${priceFromPriceList} for price list ID ${customerPriceListId}`);
        return priceFromPriceList;
      }
    }

    // For inventory items, we need to map the price list ID to the appropriate price field
    // Based on the database schema and supplier pricing logic
    console.log(`Falling back to inventory pricing for price list ID ${customerPriceListId}`);
    switch (customerPriceListId) {
      case 1: // Regional Retail AUD Incl
        return inventoryItem.regional_retail_aud_incl || inventoryItem.retail_price || 0;
      case 2: // Regional Trade AUD Incl  
        return inventoryItem.regional_trade_aud_incl || inventoryItem.trade_price || 0;
      case 3: // Metro Retail AUD Incl
        return inventoryItem.metro_retail_aud_incl || inventoryItem.retail_price || 0;
      case 4: // Metro Trade AUD Incl
        return inventoryItem.metro_trade_aud_incl || inventoryItem.trade_price || 0;
      case 5: // Regional Retail VIP AUD Incl
        return inventoryItem.regional_retail_vip_aud_incl || inventoryItem.retail_price || 0;
      case 6: // Regional Trade VIP1 AUD Incl
        return inventoryItem.regional_trade_vip1_aud_incl || inventoryItem.trade_price || 0;
      case 7: // Regional Trade VIP2 AUD Incl
        return inventoryItem.regional_trade_vip2_aud_incl || inventoryItem.trade_price || 0;
      case 8: // Metro Retail VIP AUD Incl
        return inventoryItem.metro_retail_vip_aud_incl || inventoryItem.retail_price || 0;
      case 9: // Metro Trade VIP1 AUD Incl
        return inventoryItem.metro_trade_vip1_aud_incl || inventoryItem.trade_price || 0;
      case 10: // Metro Trade VIP2 AUD Incl
        return inventoryItem.metro_trade_vip2_aud_incl || inventoryItem.trade_price || 0;
      default:
        // Fall back to trade_price if price list ID is not recognized
        return inventoryItem.trade_price || 0;
    }
  };

  const handleItemChange = (index: number, field: string, value: any) => {
    console.log(`CreateQuotePage - Item change: index=${index}, field=${field}, value=${value}`);
    const newItems = [...items];

    if (field.startsWith('quantity.')) {
      const quantityField = field.split('.')[1];
      
      // Handle empty string or invalid input
      if (value === '' || value === null || value === undefined) {
        // Special handling for BLM field - when BLM is cleared, also clear M2
        if (quantityField === 'blm') {
          newItems[index].quantity = {
            ...newItems[index].quantity,
            blm: null,
            m2: null // Clear M2 when BLM is cleared
          };
        } else {
          newItems[index].quantity = {
            ...newItems[index].quantity,
            [quantityField]: null
          };
        }
        
        // Clear M2 rounding result if M2 field is cleared
        if (quantityField === 'm2') {
          newItems[index].m2RoundingResult = undefined;
        }
        
        // Recalculate total
        let total = 0;
        if (newItems[index].quantity.units) total += Number(newItems[index].quantity.units);
        if (newItems[index].quantity.boxes) total += Number(newItems[index].quantity.boxes);
        if (newItems[index].quantity.pieces) total += Number(newItems[index].quantity.pieces);
        if (newItems[index].quantity.m2) total += Number(newItems[index].quantity.m2);
        
        newItems[index].quantity.total = total;
        newItems[index].totalPrice = total * newItems[index].unitPrice * 1.1; // Include GST (10%)
        
        setItems(newItems);
        return;
      }
      
      // For M2 field, allow any string input (including partial numbers like "1.")
      if (quantityField === 'm2') {
        // Store the raw value to allow typing
        newItems[index].quantity.m2 = value;
        
        // Only process if it's a valid number
        const numericValue = parseFloat(value);
        if (!isNaN(numericValue) && numericValue > 0) {
          // Apply M2 carton rounding logic
          let finalQuantity = numericValue;
          let roundingResult: M2RoundingResult | undefined;

          if (newItems[index].cartonConstraint?.hasConstraints) {
            roundingResult = calculateM2Rounding(numericValue, newItems[index].cartonConstraint!);
            finalQuantity = roundingResult.roundedM2;
            newItems[index].m2RoundingResult = roundingResult;
          }

          // Calculate boxes based on M2 rounding
          let calculatedBoxes = null;
          if (roundingResult && roundingResult.cartonCount > 0) {
            calculatedBoxes = roundingResult.cartonCount;
          }

          // Clear other fields when M2 is active
          newItems[index].quantity = {
            units: null,
            boxes: calculatedBoxes || null,
            pieces: null,
            m2: value, // Keep the raw input value
            blm: null,
            total: finalQuantity
          };

          // Store whether boxes was set programmatically
          if (calculatedBoxes !== null) {
            newItems[index].boxesFromM2Rounding = true;
          }
        } else {
          // Invalid number, just update the field but don't clear others
          newItems[index].quantity.total = 0;
        }
        
        newItems[index].totalPrice = newItems[index].quantity.total * newItems[index].unitPrice * 1.1; // Include GST (10%)
        setItems(newItems);
        return;
      }
      
      // For other fields, use the original logic
      const numericValue = parseFloat(value);
      if (!isNaN(numericValue) && numericValue > 0) {
        let finalQuantity = numericValue;
        let roundingResult: M2RoundingResult | undefined;

        // Apply M2 carton rounding if this is an M2 quantity change
        if (quantityField === 'm2' && newItems[index].cartonConstraint?.hasConstraints) {
          roundingResult = calculateM2Rounding(numericValue, newItems[index].cartonConstraint!);
          finalQuantity = roundingResult.roundedM2; // Use rounded value for calculations
          newItems[index].m2RoundingResult = roundingResult;
        }

        // Handle BLM: calculate M2 equivalent and set it in M2 field
        if (quantityField === 'blm') {
          const m2Equivalent = numericValue * 3.66;
          newItems[index].quantity = {
            units: null,
            boxes: null,
            pieces: null,
            m2: m2Equivalent, // Auto-fill M2 with BLM conversion
            blm: numericValue,
            total: m2Equivalent // Total should be the M2 equivalent
          };
        } else {
          // Calculate boxes based on M2 rounding
          let calculatedBoxes = null;
          if (quantityField === 'm2' && roundingResult && roundingResult.cartonCount > 0) {
            calculatedBoxes = roundingResult.cartonCount;
        }

        newItems[index].quantity = {
          units: quantityField === 'units' ? numericValue : null,
            boxes: quantityField === 'boxes' ? numericValue : (calculatedBoxes || null),
          pieces: quantityField === 'pieces' ? numericValue : null,
          m2: quantityField === 'm2' ? numericValue : null, // Store original user input, not rounded value
            blm: quantityField === 'blm' ? null : newItems[index].quantity.blm, // Clear BLM when other fields are active (except when setting BLM)
          total: finalQuantity // Use rounded value for total calculations
        };

          // Store whether boxes was set programmatically for M2 rounding
          if (calculatedBoxes !== null) {
            newItems[index].boxesFromM2Rounding = true;
          } else if (quantityField === 'boxes') {
            newItems[index].boxesFromM2Rounding = false;
          }
        newItems[index].quantity = {
          units: quantityField === 'units' ? numericValue : null,
          boxes: quantityField === 'boxes' ? numericValue : null,
          pieces: quantityField === 'pieces' ? numericValue : null,
          m2: quantityField === 'm2' ? numericValue : null, // Store original user input, not rounded value
            blm: null, // Clear BLM when other fields are active
            total: finalQuantity
        };
        }
      } else {
        // If clearing the current field, just update that field
        newItems[index].quantity = {
          ...newItems[index].quantity,
          [quantityField]: numericValue
        };

        // Clear M2 rounding result if M2 field is cleared
        if (quantityField === 'm2') {
          newItems[index].m2RoundingResult = undefined;
        }

        // Recalculate total if no specific field is active
        let total = 0;
        if (newItems[index].quantity.units) total += Number(newItems[index].quantity.units);
        if (newItems[index].quantity.boxes) total += Number(newItems[index].quantity.boxes);
        if (newItems[index].quantity.pieces) total += Number(newItems[index].quantity.pieces);
        if (newItems[index].quantity.m2) total += Number(newItems[index].quantity.m2);
        // Note: M2 includes BLM conversion when BLM is active

        newItems[index].quantity.total = total;
      }
      
      // Recalculate total price for this item
      newItems[index].totalPrice = newItems[index].quantity.total * newItems[index].unitPrice * 1.1; // Include GST (10%)
    } else if (field === 'sku') {
      console.log(`CreateQuotePage - Processing SKU change for index ${index}, SKU: ${value}`);
      newItems[index].sku = value;
      // Clear stale description and notes immediately so UI does not show previous SKU's data
      newItems[index].description = '';
      newItems[index].notes = '';

      // If SKU is entered, fetch data from both inventory and supplier sources
      if (value && value.trim().length > 0) {
        console.log(`CreateQuotePage - Finding inventory item for SKU: ${value}`);
        
        // Step 1: Get SKU details from Inventory (for description, image, etc.)
        const inventoryItem = inventoryItems.find(item => item.sku_code === value);
        if (inventoryItem) {
          console.log(`CreateQuotePage - Found inventory item for SKU ${value}:`, inventoryItem);
          // Update description from inventory
          newItems[index].description = inventoryItem.description || inventoryItem.style_code || '';
          
          // Extract and store carton constraints
          const cartonConstraint = getCartonConstraints(inventoryItem);
          newItems[index].cartonConstraint = cartonConstraint;
          setCartonConstraints(prev => ({
            ...prev,
            [value]: cartonConstraint
          }));
          
          console.log(`CreateQuotePage - Carton constraints for SKU ${value}:`, cartonConstraint);
        } else {
          console.warn(`CreateQuotePage - No inventory item found for SKU: ${value}`);
          // Clear carton constraints if no inventory item found
          newItems[index].cartonConstraint = undefined;
          setCartonConstraints(prev => {
            const newConstraints = { ...prev };
            delete newConstraints[value];
            return newConstraints;
          });
        }

        // Step 2: Get pricing from Supplier data (priority source for pricing)
        const fetchSupplierPricing = async () => {
          try {
            console.log(`Fetching supplier pricing for SKU: ${value}`);
            const supplierPricingResponse = await supplierService.getSkuPricing(value);

            if (supplierPricingResponse.success && Object.keys(supplierPricingResponse.price_list).length > 0) {
              // Use supplier pricing with customer's price list
              const customerPriceListId = selectedCustomer?.price_list_id;

              if (customerPriceListId && supplierPricingResponse.price_list[customerPriceListId.toString()]) {
                const priceForCustomer = supplierPricingResponse.price_list[customerPriceListId.toString()];

                // Update unit price with customer price list value - always use the price list value
                newItems[index].unitPrice = priceForCustomer;
                newItems[index].totalPrice = newItems[index].quantity.total * priceForCustomer * 1.1; // Include GST (10%)

                console.log(`SKU ${value}: Using supplier price ${priceForCustomer} for price list ID ${customerPriceListId}`);

                // Always prefer supplier-provided description for consistency
                if (supplierPricingResponse.description) {
                  newItems[index].description = supplierPricingResponse.description;
                }
                
                // Store notes from supplier pricing response
                if (supplierPricingResponse.notes) {
                  newItems[index].notes = supplierPricingResponse.notes;
                }
              } else {
                console.log(`SKU ${value}: No pricing found for customer price list ID ${customerPriceListId}`);
                
                // Fallback: Use inventory pricing if no matching price list ID found
                if (inventoryItem && newItems[index].unitPrice === 0) {
                  const priceForCustomer = getPriceForCustomerPriceList(inventoryItem, customerPriceListId);
                  newItems[index].unitPrice = priceForCustomer;
                  newItems[index].totalPrice = newItems[index].quantity.total * priceForCustomer * 1.1; // Include GST (10%)
                  console.log(`SKU ${value}: No matching price list found, using inventory fallback price ${priceForCustomer}`);
                }
              }
            } else {
              console.log(`SKU ${value}: No supplier pricing found, falling back to inventory pricing`);

              // Fallback: Use inventory pricing if no supplier pricing found
              if (inventoryItem && newItems[index].unitPrice === 0) {
                const customerPriceListId = selectedCustomer?.price_list_id;
                const priceForCustomer = getPriceForCustomerPriceList(inventoryItem, customerPriceListId);

                newItems[index].unitPrice = priceForCustomer;
                newItems[index].totalPrice = newItems[index].quantity.total * priceForCustomer * 1.1; // Include GST (10%)

                console.log(`SKU ${value}: Using inventory fallback price ${priceForCustomer} for price list ID ${customerPriceListId}`);
              }
              
              // Still try to get notes even if pricing failed
              if (supplierPricingResponse.notes) {
                newItems[index].notes = supplierPricingResponse.notes;
              }
            }

            // Update state and recalculate totals
            setItems([...newItems]);
            const newTotals = calculateTotals([...newItems], totals.shipping);
            setTotals(newTotals);

          } catch (error) {
            console.error('Error fetching supplier pricing:', error);

            // Fallback to inventory pricing on error
            if (inventoryItem && newItems[index].unitPrice === 0) {
              const customerPriceListId = selectedCustomer?.price_list_id;
              const priceForCustomer = getPriceForCustomerPriceList(inventoryItem, customerPriceListId);

              newItems[index].unitPrice = priceForCustomer;
              newItems[index].totalPrice = newItems[index].quantity.total * priceForCustomer * 1.1; // Include GST (10%)

              console.log(`SKU ${value}: Error fallback - using inventory price ${priceForCustomer}`);

              // Update state and recalculate totals
              setItems([...newItems]);
              const newTotals = calculateTotals([...newItems], totals.shipping);
              setTotals(newTotals);
            }
          }
        };

        // Execute the pricing fetch
        fetchSupplierPricing();

        // Also fetch from inventory API to ensure data is accurate and up-to-date (for description updates)
        const fetchInventoryData = async () => {
          try {
            const inventory = await inventoryService.getInventoryBySku(value);
            if (inventory) {
              let shouldUpdate = false;
              
              // Update description if not already set
              // Always sync description from latest inventory record
              const latestDesc = inventory.description || inventory.style_code || '';
              if (newItems[index].description !== latestDesc) {
                newItems[index].description = latestDesc;
                shouldUpdate = true;
              }

              // Update carton constraints with fresh data
              const cartonConstraint = getCartonConstraints(inventory);
              newItems[index].cartonConstraint = cartonConstraint;
              setCartonConstraints(prev => ({
                ...prev,
                [value]: cartonConstraint
              }));
              shouldUpdate = true;

              // Update state if needed
              if (shouldUpdate) {
                setItems([...newItems]);
              }
            }
          } catch (error) {
            console.error('Error fetching inventory data:', error);
            // Don't show error to user for better UX
          }
        };

        fetchInventoryData();
        
        // Update state immediately with SKU and description
        setItems([...newItems]);
      } else {
        // If SKU is cleared, reset description and pricing
        newItems[index].description = '';
        newItems[index].unitPrice = 0;
        newItems[index].totalPrice = 0;
        newItems[index].cartonConstraint = undefined;
        newItems[index].m2RoundingResult = undefined;
        setItems([...newItems]);
        // Recalculate totals immediately when SKU is cleared
        const newTotals = calculateTotals([...newItems], totals.shipping);
        setTotals(newTotals);
      }
    } else if (field === 'description') {
      newItems[index].description = value;
    } else if (field === 'unitPrice') {
      const numValue = Number(value);
      if (numValue < 0) return;
      newItems[index].unitPrice = numValue;
      newItems[index].totalPrice = newItems[index].quantity.total * numValue * 1.1;
    }

    // Always update items and recalculate totals for quantity and unitPrice changes
    if (field === 'unitPrice' || field.startsWith('quantity.') || field === 'description') {
      setItems(newItems);
      const newTotals = calculateTotals(newItems, totals.shipping);
      setTotals(newTotals);
    }
  };

  const calculateTotals = (items: QuoteItemData[], shipping: number = 0, orderTotal?: number, gst?: number) => {
    // item.totalPrice already includes GST (qty × unitPrice × 1.1)
    const calculatedOrderTotal = orderTotal !== undefined ? orderTotal :
      items.reduce((sum, item) => sum + item.totalPrice, 0);

    // Price excluding GST = total incl-GST / 1.1
    const priceExclGst = calculatedOrderTotal / 1.1;

    // GST on items = orderTotal - priceExclGst
    const itemGst = gst !== undefined ? gst : calculatedOrderTotal * (0.1 / 1.1);

    // GST: will show sum total of GST column + 10% of the shipping amount (if any)
    const calculatedGst = itemGst + (shipping * 0.1);

    // Grand Total (AUD) will show sum of Total Order: + Shipping: + GST:
    const grandTotal = priceExclGst + shipping + calculatedGst;

    return {
      orderTotal: calculatedOrderTotal,
      priceExclGst,
      gst: calculatedGst,
      shipping,
      grandTotal
    };
  };

  const handleAddItem = () => {
    const newItems = [...items, {
      id: String(items.length + 1),
      sku: '',
      description: '',
      quantity: {
        units: null,
        boxes: null,
        pieces: null,
        m2: null,
          blm: null,
        total: 0
      },
      unitPrice: 0,
      totalPrice: 0
    }];

    setItems(newItems);
  };

  const handleRemoveItem = (index: number) => {
    if (items.length === 1) return;

    const newItems = items.filter((_, i) => i !== index);
    setItems(newItems);
    const newTotals = calculateTotals(newItems, totals.shipping);
    setTotals(newTotals);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Check if shipping is blank or zero
    const shipping = totals.shipping || 0;
    const isShippingBlank = shipping === 0 || shipping === null || shipping === undefined || String(shipping).trim() === '';

    if (isShippingBlank) {
      // Show shipping confirmation dialog
      setPendingSubmit(() => async () => {
        setIsLoading(true);
        setError(null);

        try {
          if (!formData.customerId) {
            throw new Error('Please select a customer');
          }

          if (!formData.storeTypeId) {
            // Auto-set store type for restricted users if missing
            if (storeFilter.isStoreRestricted && storeFilter.userStoreTypeId) {
              console.log('Auto-setting store type during validation:', storeFilter.userStoreTypeId);
              setFormData(prev => ({
                ...prev,
                storeTypeId: String(storeFilter.userStoreTypeId)
              }));
              // Continue with the updated store type
            } else {
            throw new Error('Please select a store type');
            }
          }

          if (items.length === 0 || items.some(item => !item.sku)) {
            throw new Error('Please add at least one item with SKU');
          }

          // For API request, convert to number if it looks like a numeric string
          const customerId = /^\d+$/.test(formData.customerId)
            ? parseInt(formData.customerId, 10)
            : formData.customerId;

          // Convert store type ID to number, with fallback for restricted users
          let storeTypeIdToUse = formData.storeTypeId;
          if (!storeTypeIdToUse && storeFilter.isStoreRestricted && storeFilter.userStoreTypeId) {
            console.log('Using fallback store type for restricted user:', storeFilter.userStoreTypeId);
            storeTypeIdToUse = String(storeFilter.userStoreTypeId);
          }
          const storeTypeId = parseInt(storeTypeIdToUse, 10);

          const requestData: QuoteCreate = {
            company_id: formData.companyId as string,
            store_type_id: storeTypeId,
            customer_id: customerId,  // Could be string or number now
            deliver_to_address: formData.deliverToAddress,
            date: formData.date,
            notes: formData.notes || '',
            status: "saved", // Set status to saved when submitting
            shipping: totals.shipping, // Add shipping field from totals state
            items: items.map(item => {
              const quoteItem: QuoteItemCreate = {
                sku_code: item.sku,
                description: item.description || `Item ${item.sku}`, // Provide default description
                quantity_units: item.quantity.units || 0,
                quantity_boxes: item.quantity.boxes || 0,
                quantity_pieces: item.quantity.pieces || 0,
                quantity_m2: item.quantity.m2 || 0,
                quantity_blm: item.quantity.blm || 0,
                unit_price: item.unitPrice
              };
              return quoteItem;
            })
          };

          console.log('Sending quote data to API:', requestData);

          let response;
          if (isEditing && editQuoteId) {
            // Update existing quote
            response = await quoteService.updateQuote(editQuoteId, requestData);
            console.log('Quote updated successfully:', response);
          } else {
            // Create new quote
            response = await quoteService.createQuote(requestData);
            console.log('Quote created successfully:', response);
          }

          // Show success message before navigation
          console.log(`Quote ${isEditing ? 'updated' : 'created'} successfully, navigating back...`);
          
          // Navigate back immediately with refresh flag
          navigate('/quotes', { state: { refresh: true }, replace: true });
          setHasUnsavedChanges(false);
        } catch (error) {
          handleError(error, 'save quote');
        } finally {
          setIsLoading(false);
        }
      });
      setShowShippingConfirmation(true);
      return;
    }

    // If shipping is provided, proceed with submission
    setIsLoading(true);
    setError(null);

    try {
      if (!formData.customerId) {
        throw new Error('Please select a customer');
      }

      if (!formData.storeTypeId) {
        // Auto-set store type for restricted users if missing
        if (storeFilter.isStoreRestricted && storeFilter.userStoreTypeId) {
          console.log('Auto-setting store type during validation:', storeFilter.userStoreTypeId);
          setFormData(prev => ({
            ...prev,
            storeTypeId: String(storeFilter.userStoreTypeId)
          }));
          // Continue with the updated store type
        } else {
        throw new Error('Please select a store type');
        }
      }

      if (items.length === 0 || items.some(item => !item.sku)) {
        throw new Error('Please add at least one item with SKU');
      }

      // For API request, convert to number if it looks like a numeric string
      const customerId = /^\d+$/.test(formData.customerId)
        ? parseInt(formData.customerId, 10)
        : formData.customerId;

      // Convert store type ID to number, with fallback for restricted users
      let storeTypeIdToUse = formData.storeTypeId;
      if (!storeTypeIdToUse && storeFilter.isStoreRestricted && storeFilter.userStoreTypeId) {
        console.log('Using fallback store type for restricted user:', storeFilter.userStoreTypeId);
        storeTypeIdToUse = String(storeFilter.userStoreTypeId);
      }
      const storeTypeId = parseInt(storeTypeIdToUse, 10);

      const requestData: QuoteCreate = {
        company_id: formData.companyId as string,
        store_type_id: storeTypeId,
        customer_id: customerId,  // Could be string or number now
        deliver_to_address: formData.deliverToAddress,
        date: formData.date,
        notes: formData.notes || '',
        status: "saved", // Set status to saved when submitting
        shipping: totals.shipping, // Add shipping field from totals state
        items: items.map(item => {
          const quoteItem: QuoteItemCreate = {
            sku_code: item.sku,
            description: item.description || `Item ${item.sku}`, // Provide default description
            quantity_units: item.quantity.units || 0,
            quantity_boxes: item.quantity.boxes || 0,
            quantity_pieces: item.quantity.pieces || 0,
            quantity_m2: item.quantity.m2 || 0,
            quantity_blm: item.quantity.blm || 0,
            unit_price: item.unitPrice
          };
          return quoteItem;
        })
      };

      console.log('Sending quote data to API:', requestData);

      let response;
      if (isEditing && editQuoteId) {
        // Update existing quote
        response = await quoteService.updateQuote(editQuoteId, requestData);
        console.log('Quote updated successfully:', response);
      } else {
        // Create new quote
        response = await quoteService.createQuote(requestData);
        console.log('Quote created successfully:', response);
      }

      // Show success message before navigation
      console.log(`Quote ${isEditing ? 'updated' : 'created'} successfully, navigating back...`);
      
      // Navigate back immediately with refresh flag
      navigate('/quotes', { state: { refresh: true }, replace: true });
      setHasUnsavedChanges(false);
    } catch (error) {
      handleError(error, 'save quote');
    } finally {
      setIsLoading(false);
    }
  };

  const handleShippingConfirm = () => {
    setShowShippingConfirmation(false);
    if (pendingSubmit) {
      pendingSubmit();
      setPendingSubmit(null);
    }
  };

  const handleShippingCancel = () => {
    setShowShippingConfirmation(false);
    setPendingSubmit(null);
  };

  // Update handleSaveAsDraft for editing
  const handleSaveAsDraft = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      // For API request, convert to number if it looks like a numeric string
      const customerId = formData.customerId ? 
        (/^\d+$/.test(formData.customerId) ? parseInt(formData.customerId, 10) : formData.customerId) : 
        "";

      // Convert store type ID to number if available, with fallback for restricted users
      let storeTypeIdToUse = formData.storeTypeId;
      if (!storeTypeIdToUse && storeFilter.isStoreRestricted && storeFilter.userStoreTypeId) {
        console.log('Using fallback store type for restricted user in draft:', storeFilter.userStoreTypeId);
        storeTypeIdToUse = String(storeFilter.userStoreTypeId);
      }
      const storeTypeId = storeTypeIdToUse ? parseInt(storeTypeIdToUse, 10) : 1; // Default to 1 if not provided

      const requestData: QuoteCreate = {
        company_id: formData.companyId as string || "1", // Default to company 1 if not provided
        store_type_id: storeTypeId,
        customer_id: customerId,
        deliver_to_address: formData.deliverToAddress || "",
        date: formData.date || new Date().toISOString().split('T')[0],
        notes: formData.notes || '',
        status: "draft", // Set status to draft for drafts
        shipping: totals.shipping, // Add shipping field from totals state
        items: items.map(item => {
          const quoteItem: QuoteItemCreate = {
            sku_code: item.sku || "",
            description: item.description || `Item`, // Provide default description
            quantity_units: item.quantity.units || 0,
            quantity_boxes: item.quantity.boxes || 0,
            quantity_pieces: item.quantity.pieces || 0,
            quantity_m2: item.quantity.m2 || 0,
            quantity_blm: item.quantity.blm || 0,
            unit_price: item.unitPrice || 0
          };
          return quoteItem;
        })
      };

      console.log('Saving quote as draft:', requestData);

      let response;
      if (isEditing && editQuoteId) {
        // Update existing quote
        response = await quoteService.updateQuote(editQuoteId, requestData);
        console.log('Quote draft updated successfully:', response);
      } else {
        // Create new quote
        response = await quoteService.createQuote(requestData);
        console.log('Quote draft saved successfully:', response);
      }

      // Show success message before navigation
      console.log(`Quote draft ${isEditing ? 'updated' : 'saved'} successfully, navigating back...`);
      
      // Navigate back immediately with refresh flag
      navigate('/quotes', { state: { refresh: true }, replace: true });
    } catch (error) {
      handleError(error, 'save quote as draft');
    } finally {
      setIsLoading(false);
    }
  };

  // Function to format currency
  const formatCurrency = (amount: number) => {
    return !isNaN(amount) ? `$${amount.toFixed(2)}` : '$0.00';
  };

  // Add function to handle shipping modal
  const handleShippingInfoClick = () => {
    setIsShippingModalOpen(true);
  };

  const handleCloseShippingModal = () => {
    setIsShippingModalOpen(false);
  };

  const handleCartonInfoHover = (itemIndex: number, show: boolean) => {
    setCartonInfoTooltip({
      visible: show,
      itemIndex: show ? itemIndex : null
    });
  };

  // Add effect to fetch store types and inventory items
  useEffect(() => {
    fetchStoreTypes();
    fetchInventoryItems();
  }, []);

  // Auto-set store type for restricted users (staff/manager)
  useEffect(() => {
    // Only set if form is not in editing mode and user is store restricted
    if (!isEditing && storeFilter.isStoreRestricted && storeFilter.userStoreTypeId) {
      console.log('Auto-setting store type for restricted user:', storeFilter.userStoreTypeId);
      setFormData(prev => ({
        ...prev,
        storeTypeId: storeFilter.userStoreTypeId?.toString() || ''
      }));
    }
  }, [storeFilter.isStoreRestricted, storeFilter.userStoreTypeId, isEditing, totals.shipping]);

  // Add effect to recalculate totals whenever items change
  useEffect(() => {
    const newTotals = calculateTotals(items, totals.shipping);
    setTotals(newTotals);
  }, [items]); // Only depend on items, not totals to avoid infinite loop

  // Auto-refresh inventory data every 5 minutes to pick up newly uploaded images (FIXED: Reduced frequency)
  useEffect(() => {
    // Only set up auto-refresh if user is actively working on the page
    const interval = setInterval(() => {
      // Only auto-refresh if page is visible, not currently loading, and user has interacted recently
      if (!document.hidden && !isLoadingInventory && document.hasFocus()) {
        console.log('Auto-refreshing inventory data...');
        fetchInventoryItems(true);
      }
    }, 300000); // FIXED: Changed from 30 seconds to 5 minutes (300000ms)

    return () => clearInterval(interval);
  }, [isLoadingInventory]);

  // Refresh inventory when page becomes visible (user returns from other tabs/windows)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && !isLoadingInventory) {
        console.log('Page became visible, refreshing inventory data...');
        fetchInventoryItems(true);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleVisibilityChange);
    };
  }, [isLoadingInventory]);

  // Function to fetch store types
  const fetchStoreTypes = async () => {
    setIsLoadingStoreTypes(true);
    try {
      const response = await storeTypeService.getStoreTypes();
      setStoreTypes(response);
      console.log('Successfully loaded store types:', response.length);
    } catch (error) {
      console.error('Error fetching store types:', error);
      // Use default store types if API fails
      setStoreTypes([
        { id: 1, name: 'Cranbourne' },
        { id: 2, name: 'Sale' }
      ]);
    } finally {
      setIsLoadingStoreTypes(false);
    }
  };

  // Function to fetch inventory items
  const fetchInventoryItems = async (force: boolean = false) => {
    setIsLoadingInventory(true);
    try {
      console.log('CreateQuotePage - Fetching inventory items, force:', force);
      
      // Clear cache if force refreshing to ensure fresh data
      if (force) {
        inventoryService.clearInventoryCache();
      }

      // Just load a minimal set of items for initial display
      // Full search will be handled by the backend search
      const response = await inventoryService.getInventoryItems({ limit: 20, active_only: true });
      
      if (response && response.length > 0) {
        console.log('CreateQuotePage - Fetched minimal inventory set:', response.length);
        setInventoryItems(response);
      } else {
        console.warn('CreateQuotePage - Failed to fetch initial inventory items');
        setInventoryItems([]);
      }

      if (force) {
        setLastInventoryRefresh(Date.now());
        console.log('Inventory data refreshed with latest images');
      }
    } catch (error) {
      console.error('CreateQuotePage - Error fetching inventory items:', error);
      setInventoryItems([]);
    } finally {
      setIsLoadingInventory(false);
    }
  };



  const handleShippingChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    
    // Only allow numeric characters, decimal point, and empty string
    const numericRegex = /^(\d*\.?\d*)$/;
    if (inputValue === '' || numericRegex.test(inputValue)) {
      // Parse the numeric value from input
      const value = inputValue === '' ? 0 : parseFloat(inputValue) || 0;
      // Don't pass the old GST value - let calculateTotals recalculate it
      const newTotals = calculateTotals(items, value, totals.orderTotal);
      setTotals(newTotals);
    }
  };

  // Auto-populate store type for restricted users when user data loads
  useEffect(() => {
    // Only update if user is store-restricted and we don't already have a store type set
    if (storeFilter.isStoreRestricted && storeFilter.userStoreTypeId && !formData.storeTypeId) {
      console.log('Auto-setting store type for restricted user in quote:', storeFilter.userStoreTypeId);
      setFormData(prev => ({
        ...prev,
        storeTypeId: String(storeFilter.userStoreTypeId)
      }));
    }
  }, [storeFilter.isStoreRestricted, storeFilter.userStoreTypeId, formData.storeTypeId]);

  // Fetch quote data for editing when editQuoteId is provided
  useEffect(() => {
    const fetchQuoteForEditing = async () => {
      if (!editQuoteId) return;
      
      // Wait for store types to be loaded before fetching quote data
      if (storeTypes.length === 0) return;
      
      setIsLoading(true);
      setIsEditing(true);
      
      try {
        const quoteData = await quoteService.getQuoteById(editQuoteId);
        console.log('Fetched quote for editing:', quoteData);
        
        // Find store type ID by name if store_type_id is not available
        let storeTypeId = '';
        if (quoteData.store_type_id) {
          storeTypeId = quoteData.store_type_id.toString();
        } else if (quoteData.store_type && storeTypes.length > 0) {
          const matchingStoreType = storeTypes.find(st => st.name === quoteData.store_type);
          storeTypeId = matchingStoreType ? matchingStoreType.id.toString() : '';
          console.log('Found matching store type:', matchingStoreType, 'for store type name:', quoteData.store_type);
        }
        
        console.log('Setting storeTypeId to:', storeTypeId);
        
        // Check if delivery address is "Customer Pickup"
        const isCustomerPickup = quoteData.deliver_to_address === 'Customer Pickup';
        setCustomerPickup(isCustomerPickup);

        // Populate form data
        setFormData({
          storeTypeId: storeTypeId,
          customerId: quoteData.customer_id?.toString() || '',
          companyId: quoteData.company_id || 'c6c6b678-dc5c-4693-9a6e-d22e1b97dd05',
          companyName: quoteData.customer_name || '',
          deliverToAddress: quoteData.deliver_to_address || '',
          date: quoteData.date || new Date().toISOString().split('T')[0],
          notes: quoteData.notes || ''
        });
        
        // Fetch customer details to get price list information
        if (quoteData.customer_id) {
          try {
            const customer = await customerService.getCustomerById(quoteData.customer_id.toString());
            setSelectedCustomer(customer);
          } catch (error) {
            console.error('Error fetching customer details:', error);
          }
        }
        
        // Populate quote items
        if (quoteData.quote_items && quoteData.quote_items.length > 0) {
          const formattedItems = quoteData.quote_items.map(item => ({
            id: item.id || String(Math.random()),
            sku: item.sku_code || item.sku || '',
            description: item.description || '',
            quantity: {
              units: item.quantity_units || item.units || 0,
              boxes: item.quantity_boxes || item.boxes || 0,
              pieces: item.quantity_pieces || item.pieces || 0,
              m2: item.quantity_m2 || item.m2 || 0,
              blm: item.quantity_blm || item.blm || 0,
              total: (item.quantity_units || item.units || 0) + 
                     (item.quantity_boxes || item.boxes || 0) + 
                     (item.quantity_pieces || item.pieces || 0) + 
                     (item.quantity_m2 || item.m2 || 0)
                     // BLM is excluded from total calculation as per requirements
            },
            unitPrice: item.unit_price || 0,
            totalPrice: item.total_price || item.unit_price * ((item.quantity_units || item.units || 0) + 
                                                              (item.quantity_boxes || item.boxes || 0) + 
                                                              (item.quantity_pieces || item.pieces || 0) + 
                                                              (item.quantity_m2 || item.m2 || 0))
          }));
          
          setItems(formattedItems);
        }
        
        // Set totals
        console.log('Setting totals from quote data:', {
          orderTotal: quoteData.total_order,
          gst: quoteData.total_gst,
          shipping: quoteData.shipping,
          grandTotal: quoteData.grand_total
        });
        
        setTotals({
          orderTotal: quoteData.total_order || 0,
          priceExclGst: (quoteData.total_order || 0) / 1.1,
          gst: quoteData.total_gst || 0,
          shipping: quoteData.shipping || 0,
          grandTotal: quoteData.grand_total || 0
        });
        
      } catch (error) {
        console.error('Error fetching quote for editing:', error);
        setError('Failed to load quote for editing. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchQuoteForEditing();
  }, [editQuoteId, storeTypes]); // Add storeTypes as dependency

  return (
    <Layout>
      <PageContainer>
        <BreadcrumbContainer>
          <BreadcrumbNav>
            <BackButton onClick={() => {
              if (returnTo) {
                navigate(decodeURIComponent(returnTo));
              } else {
                navigate('/quotes');
              }
            }}>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            </BackButton>
            <QuotesText to={returnTo ? decodeURIComponent(returnTo) : "/quotes"}>
              {returnTo ? 'Customer History' : 'Quotes'}
            </QuotesText>
            <BreadcrumbSeparator>/</BreadcrumbSeparator>
            <CreateQuoteTitle>{isEditing ? 'Edit Quote' : 'Create New Quote'}</CreateQuoteTitle>
          </BreadcrumbNav>
        </BreadcrumbContainer>

        {error && (
          <ErrorMessage>
            {error}
          </ErrorMessage>
        )}

        <FormContainer onSubmit={handleSubmit}>
          <FormRow>
            <FormColumn>
              <FormLabel>Store Type<span className="required">*</span></FormLabel>
              <SelectWrapper>
                <FormSelect
                  name="storeTypeId"
                  value={formData.storeTypeId}
                  onChange={handleInputChange}
                  required
                  disabled={isLoadingStoreTypes || storeFilter.isStoreRestricted}
                  style={{
                    backgroundColor: storeFilter.isStoreRestricted ? '#F9FAFB' : 'white',
                    cursor: storeFilter.isStoreRestricted ? 'not-allowed' : 'pointer'
                  }}
                >
                  {!storeFilter.isStoreRestricted && (
                    <option value="">Select Store Type</option>
                  )}
                  {storeTypes.map(type => (
                    <option key={type.id} value={type.id}>{type.name}</option>
                  ))}
                </FormSelect>
              </SelectWrapper>
              {storeFilter.isStoreRestricted && (
                <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '4px' }}>
                  Store type is auto-selected based on your role
                </div>
              )}
            </FormColumn>

            <FormColumn>
              <FormLabel>Quote To</FormLabel>
              <CustomerSelectWrapper>
                <CustomerSelect
                  name="customerId"
                  value={formData.customerId.toString()}
                  onChange={handleCustomerSelect}
                  required
                  placeholder="Search Customer"
                />
              </CustomerSelectWrapper>
            </FormColumn>

            <FormColumn>
              <FormLabel>Notes</FormLabel>
              <NotesTextarea
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
              />
            </FormColumn>
          </FormRow>

          <FormRow>
            <FormColumn>
              <FormLabel>Date</FormLabel>
              <DateInput
                type="date"
                name="date"
                value={formData.date}
                onChange={handleInputChange}
                required
              />
            </FormColumn>

            <FormColumn>
              <FormLabel>Deliver to Address</FormLabel>
              <DeliverToAddressContainer>
                <DeliverToAddressInputWrapper>
                  <DeliverToAddressInput
                    type="text"
                    name="deliverToAddress"
                    value={formData.deliverToAddress}
                    onChange={handleInputChange}
                    disabled={customerPickup}
                  />
                </DeliverToAddressInputWrapper>
                <CheckboxContainer>
                  <CheckboxInput
                    type="checkbox"
                    id="customerPickup"
                    checked={customerPickup}
                    onChange={handleCustomerPickupChange}
                  />
                  <CheckboxLabel htmlFor="customerPickup">
                    Customer Pickup
                  </CheckboxLabel>
                </CheckboxContainer>
              </DeliverToAddressContainer>
            </FormColumn>

            <FormColumn>
              {/* Empty column for alignment */}
            </FormColumn>
          </FormRow>

          <TableContainer>
            <Table>
              <TableHeader>
                <tr>
                  <th style={{ width: '60px' }} rowSpan={2}>
                    Notes
                  </th>
                  <th style={{ width: '180px' }} rowSpan={2}>
                    BWA SKU
                  </th>
                  <th style={{ width: '250px' }} rowSpan={2}>Description</th>
                  <th colSpan={6} style={{ textAlign: 'center' }}>Quantity</th>
                  <th style={{ width: '130px', textAlign: 'right' }} rowSpan={2}>Unit Price<br />excl GST</th>
                  <th style={{ width: '140px', textAlign: 'right' }} rowSpan={2}>Total Price<br />(excluding GST)</th>
                  <th style={{ width: '65px', textAlign: 'right' }} rowSpan={2}>GST</th>
                  <th style={{ width: '140px', textAlign: 'right' }} rowSpan={2}>Total Price<br />(including GST)</th>
                  <th style={{ width: '80px' }} rowSpan={2}></th>
                </tr>
                <tr>
                  <th style={{ width: '80px', textAlign: 'center' }}>Units</th>
                  <th style={{ width: '80px', textAlign: 'center' }}>Boxes</th>
                  <th style={{ width: '80px', textAlign: 'center' }}>Pieces</th>
                  <th style={{ width: '80px', textAlign: 'center' }}>M2</th>
                  <th style={{ width: '80px', textAlign: 'center' }}>BLM</th>
                  <th style={{ width: '80px', textAlign: 'center' }}>Total</th>
                </tr>
              </TableHeader>
              <TableBody>
                {items.length === 0 ? (
                  <tr>
                    <td colSpan={14} style={{ textAlign: 'center' }}>
                      <AddButton 
                        type="button" 
                        onClick={handleAddItem}
                        disabled={!formData.customerId || formData.customerId.trim() === '' || !formData.storeTypeId || formData.storeTypeId.trim() === ''}
                        title={(!formData.customerId || formData.customerId.trim() === '' || !formData.storeTypeId || formData.storeTypeId.trim() === '') ? 'Please fill in Store Type and Quote To fields first' : 'Add item'}
                      >+</AddButton>
                    </td>
                  </tr>
                ) : (
                  items.map((item, index) => {
                  const activeQuantityField = getActiveQuantityField(item.quantity, item);
                  const isSkuDisabled = !formData.customerId || formData.customerId.trim() === '' || !formData.storeTypeId || formData.storeTypeId.trim() === '';
                  const isFieldDisabled = isSkuDisabled || !item.sku;
                  
                  return (
                    <tr key={item.id}>
                      <td style={{ textAlign: 'center', padding: '8px 4px' }}>
                        <NotesIcon
                          hasNotes={!!item.notes && item.notes.length > 0}
                          onClick={() => {
                            if (item.sku && item.sku.trim().length > 0) {
                              setCurrentNotesItemId(item.id);
                              setCurrentNotesValue(item.notes || '');
                              setShowNotesModal(true);
                            }
                          }}
                          style={{ 
                            cursor: item.sku && item.sku.trim().length > 0 ? 'pointer' : 'not-allowed',
                            opacity: item.sku && item.sku.trim().length > 0 ? 1 : 0.5
                          }}
                        >
                          <img src={invoicesNotesIcon} alt="Notes" />
                        </NotesIcon>
                      </td>
                      <td className="wrap">
                        <SearchableSkuDropdown
                          value={item.sku}
                          onChange={(value) => handleItemChange(index, 'sku', value)}
                          inventoryItems={inventoryItems}
                          disabled={isLoadingInventory || isSkuDisabled}
                          lastInventoryRefresh={lastInventoryRefresh}
                          title={isSkuDisabled ? 'Enter Store Type and Customer first' : undefined}
                        />
                      </td>
                      <td className="wrap">
                        <FormTextarea
                          value={item.description}
                          onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                          style={{ width: '100%', minHeight: '48px' }}
                          rows={2}
                          disabled={isFieldDisabled}
                        />
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <QuantityInput
                          type="text"
                          value={item.quantity.units || ''}
                          onChange={(e) => handleItemChange(index, 'quantity.units', e.target.value)}
                          isDisabled={(activeQuantityField !== null && activeQuantityField !== 'units') || isFieldDisabled}
                          disabled={(activeQuantityField !== null && activeQuantityField !== 'units') || isFieldDisabled}
                        />
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <QuantityInput
                          type="text"
                          value={item.quantity.boxes || ''}
                          onChange={(e) => handleItemChange(index, 'quantity.boxes', e.target.value)}
                          isDisabled={(activeQuantityField !== null && activeQuantityField !== 'boxes') || isFieldDisabled}
                          disabled={(activeQuantityField !== null && activeQuantityField !== 'boxes') || isFieldDisabled}
                        />
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <QuantityInput
                          type="text"
                          value={item.quantity.pieces || ''}
                          onChange={(e) => handleItemChange(index, 'quantity.pieces', e.target.value)}
                          isDisabled={(activeQuantityField !== null && activeQuantityField !== 'pieces') || isFieldDisabled}
                          disabled={(activeQuantityField !== null && activeQuantityField !== 'pieces') || isFieldDisabled}
                        />
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <QuantityInput
                          type="text"
                          value={item.quantity.m2 || ''}
                          onChange={(e) => handleItemChange(index, 'quantity.m2', e.target.value)}
                          isDisabled={(activeQuantityField !== null && activeQuantityField !== 'm2') || isFieldDisabled}
                          disabled={(activeQuantityField !== null && activeQuantityField !== 'm2') || isFieldDisabled}
                          readOnly={activeQuantityField === 'blm' || isFieldDisabled}
                          style={activeQuantityField === 'blm' || isFieldDisabled ? { backgroundColor: '#f5f5f5', color: '#666' } : {}}
                        />
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <QuantityInput
                          type="number"
                          value={item.quantity.blm || ''}
                          onChange={(e) => {
                            // Immediately trigger full BLM logic as user types
                            handleItemChange(index, 'quantity.blm', e.target.value);
                          }}
                          isDisabled={(activeQuantityField !== null && activeQuantityField !== 'blm') || isFieldDisabled}
                          disabled={(activeQuantityField !== null && activeQuantityField !== 'blm') || isFieldDisabled}
                          step="0.01"
                          min="0"
                          placeholder="0.00"
                        />
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <QuantityInput
                            type="text"
                            value={item.quantity.total.toFixed(2)}
                            readOnly
                            style={{ backgroundColor: '#f9fafb', marginRight: '4px' }}
                          />
                          {(item.cartonConstraint?.hasConstraints || item.m2RoundingResult?.isRounded) && (
                            <SmallInfoIcon 
                              showTooltip={cartonInfoTooltip.visible && cartonInfoTooltip.itemIndex === index}
                              onMouseEnter={() => handleCartonInfoHover(index, true)}
                              onMouseLeave={() => handleCartonInfoHover(index, false)}
                              title="Carton Information"
                            >
                              i
                              <CartonInfoTooltip show={cartonInfoTooltip.visible && cartonInfoTooltip.itemIndex === index}>
                                {item.m2RoundingResult?.isRounded 
                                  ? item.m2RoundingResult.message 
                                  : item.cartonConstraint?.hasConstraints 
                                    ? formatCartonInfo(item.cartonConstraint)
                                    : 'No carton information available'
                                }
                              </CartonInfoTooltip>
                            </SmallInfoIcon>
                          )}
                        </div>
                      </td>
                      <td style={{ textAlign: 'right', padding: '4px' }}>
                        <UnitPriceInput
                          type="number"
                          value={item.unitPrice}
                          readOnly={!canEditPrice}
                          step="0.01"
                          onChange={(e) => handleItemChange(index, 'unitPrice', e.target.value)}
                          style={canEditPrice ? { 
                            width: '100%',
                            maxWidth: '130px'
                          } : { 
                            backgroundColor: '#f9fafb',
                            cursor: 'not-allowed',
                            color: '#6B7280',
                            width: '100%',
                            maxWidth: '130px'
                          }}
                        />
                      </td>
                      <td style={{ textAlign: 'right', padding: '4px' }}>
                        <UnitPriceInput
                          type="text"
                          value={(item.quantity.total * item.unitPrice).toFixed(2)}
                          readOnly
                          style={{ 
                            backgroundColor: '#f9fafb',
                            cursor: 'not-allowed',
                            color: '#6B7280',
                            width: '100%',
                            maxWidth: '140px'
                          }}
                        />
                      </td>
                      <td style={{ textAlign: 'right', padding: '4px' }}>
                        <UnitPriceInput
                          type="text"
                          value={(item.unitPrice * 0.1).toFixed(2)}
                          readOnly
                          style={{ 
                            backgroundColor: '#f9fafb',
                            cursor: 'not-allowed',
                            color: '#6B7280',
                            width: '100%',
                            maxWidth: '65px'
                          }}
                        />
                      </td>
                      <td style={{ textAlign: 'right', padding: '4px' }}>
                        <TotalPriceInput
                          type="text"
                          value={formatCurrency(item.totalPrice).replace('$', '')}
                          readOnly
                          style={{
                            width: '100%',
                            maxWidth: '140px'
                          }}
                        />
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <div style={{ display: 'flex', gap: '4px', justifyContent: 'flex-end' }}>
                          <AddButton 
                            type="button" 
                            onClick={handleAddItem}
                            disabled={!formData.customerId || formData.customerId.trim() === '' || !formData.storeTypeId || formData.storeTypeId.trim() === ''}
                            title={(!formData.customerId || formData.customerId.trim() === '' || !formData.storeTypeId || formData.storeTypeId.trim() === '') ? 'Please fill in Store Type and Quote To fields first' : 'Add item'}
                          >+</AddButton>
                          {items.length > 1 && (
                            <RemoveButton 
                              type="button" 
                              onClick={() => handleRemoveItem(index)}
                            >×</RemoveButton>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TotalSection>
            <TotalRow>
              <TotalLabel>Total Order:</TotalLabel>
              <CurrencySymbol>$</CurrencySymbol>
              <TotalInput
                type="text"
                value={(totals.priceExclGst ?? 0).toFixed(2)}
                readOnly
              />
            </TotalRow>
            <TotalRow>
              <TotalLabel>
                <ClickableInfoIcon onClick={handleShippingInfoClick}>
                  i
                </ClickableInfoIcon>
                Shipping:
              </TotalLabel>
              <CurrencySymbol>$</CurrencySymbol>
              <TotalInput
                type="number"
                value={totals.shipping || ''}
                onChange={handleShippingChange}
                placeholder="0.00"
                step="0.01"
                min="0"
              />
            </TotalRow>
            <TotalRow>
              <TotalLabel>GST:</TotalLabel>
              <CurrencySymbol>$</CurrencySymbol>
              <TotalInput
                type="text"
                value={(totals.gst ?? 0).toFixed(2)}
                readOnly
              />
            </TotalRow>
            <Divider />
            <TotalRow>
              <GrandTotalLabel>Grand Total (AUD)</GrandTotalLabel>
              <CurrencySymbol>$</CurrencySymbol>
              <GrandTotalInput
                type="text"
                value={(totals.grandTotal ?? 0).toFixed(2)}
                readOnly
              />
            </TotalRow>
          </TotalSection>

          <ButtonsContainer>
            <CancelButton type="button" onClick={handleCancel} disabled={isLoading}>
              Cancel
            </CancelButton>
            <div style={{ display: 'flex', gap: '10px' }}>
              <SaveAsDraftButton onClick={handleSaveAsDraft} disabled={isLoading}>
                {isLoading ? 'Saving...' : 'Save as Draft'}
              </SaveAsDraftButton>
              <SubmitButton onClick={handleSubmit} disabled={isLoading}>
                {isLoading ? 'Submitting...' : 'Submit'}
              </SubmitButton>
            </div>
          </ButtonsContainer>
        </FormContainer>

        {/* Shipping Details Modal */}
        {isShippingModalOpen && (
          <ModalOverlay onClick={handleCloseShippingModal}>
            <ModalContent onClick={(e) => e.stopPropagation()}>
              <ModalHeader>
                <ModalTitle>BWA DELIVERY PRICELIST</ModalTitle>
                <ModalCloseButton onClick={handleCloseShippingModal}>
                  ×
                </ModalCloseButton>
              </ModalHeader>
              <ModalBody>
                <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                  <h2 style={{ 
                    color: '#DC2626', 
                    fontWeight: 'bold', 
                    fontSize: '16px', 
                    margin: '0 0 20px 0',
                    textTransform: 'uppercase'
                  }}>
                    SUPPLIER TILE DELIVERIES TO BWA – PASS ON TO CUSTOMERS
                  </h2>
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: '1px dotted #666' }}>
                    <span><strong>1 – 3 BOXES</strong></span>
                    <span style={{ fontWeight: 'bold' }}>$30.00</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: '1px dotted #666' }}>
                    <span><strong>4 – 7 BOXES</strong></span>
                    <span style={{ fontWeight: 'bold' }}>$40.00</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: '1px dotted #666' }}>
                    <span><strong>8 – 12 BOXES</strong></span>
                    <span style={{ fontWeight: 'bold' }}>$50.00</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: '1px dotted #666' }}>
                    <span><strong>SMALL SKID (13 – 19 BOXES)</strong></span>
                    <span style={{ fontWeight: 'bold' }}>$60.00</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: '1px dotted #666' }}>
                    <span><strong>PALLET (20 - 40 BOXES)</strong></span>
                    <span style={{ fontWeight: 'bold' }}>$90.00</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: '1px dotted #666' }}>
                    <span><strong>LARGE PALLET (40 – 60 BOXES)</strong></span>
                    <span style={{ fontWeight: 'bold' }}>$100.00</span>
                  </div>
                </div>

                <div style={{ 
                  textAlign: 'center', 
                  color: '#16A34A', 
                  fontSize: '12px', 
                  fontWeight: 'bold',
                  margin: '15px 0',
                  textTransform: 'uppercase'
                }}>
                  FOR SMALLER BOX ORDERS EG 75*300MM CHARGE LESS THAN ABOVE. EG: 24 BOXES 75*300MM = $60.00
                </div>

                <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                  <h2 style={{ 
                    color: '#DC2626', 
                    fontWeight: 'bold', 
                    fontSize: '16px', 
                    margin: '0',
                    textTransform: 'uppercase'
                  }}>
                    DELIVERY FEES BWA TO CUSTOMER (TILES, BATHROOM WARE & TAPWARE)
                  </h2>
                  <div style={{ 
                    color: '#DC2626', 
                    fontWeight: 'bold', 
                    fontSize: '14px', 
                    margin: '5px 0 15px 0',
                    textTransform: 'uppercase'
                  }}>
                    (BWA DELIVERY TO CUSTOMER)
                  </div>
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: '1px dotted #666' }}>
                    <span><strong>UNDER $250.00 (EX GST)</strong></span>
                    <span style={{ fontWeight: 'bold' }}>$25.00</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: '1px dotted #666' }}>
                    <span><strong>SMALL LOAD</strong></span>
                    <span style={{ fontWeight: 'bold' }}>$70.00</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: '1px dotted #666' }}>
                    <span><strong>LARGE LOAD</strong></span>
                    <span style={{ fontWeight: 'bold' }}>$140.00</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: '1px dotted #666' }}>
                    <span><strong>BWA HALLAM TILES (PER PALLET) FORKLIFT DELIVERY</strong></span>
                    <span style={{ fontWeight: 'bold' }}>$100.00 (HALLAM CUSTOMERS ONLY)</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: '1px dotted #666' }}>
                    <span><strong>BWA SALE TILES (PER PALLET) FORKLIFT DELIVERY</strong></span>
                    <span style={{ fontWeight: 'bold' }}>OBTAIN QUOTE FROM FREIGHT CO.</span>
                  </div>
                </div>

                <div style={{ 
                  textAlign: 'center', 
                  color: '#16A34A', 
                  fontSize: '12px', 
                  fontWeight: 'bold',
                  margin: '15px 0',
                  textTransform: 'uppercase'
                }}>
                  FOR BWA SALE FORKLIFT DELIVERIES – CHECK WITH YOUR FREIGHT COMPANY & PASS ON PRICE PER PALLET
                </div>

                <div style={{ 
                  textAlign: 'center', 
                  fontSize: '12px', 
                  fontStyle: 'italic',
                  margin: '20px 0 0 0'
                }}>
                  (Note: PRICES DO NOT INCLUDE GST)
                </div>
              </ModalBody>
            </ModalContent>
          </ModalOverlay>
        )}


      </PageContainer>
      
      {/* Toast notification */}
      {toast.isVisible && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => {}}
        />
      )}

      {/* Exit Confirmation Dialog */}
      <ExitConfirmationDialog
        isOpen={showExitConfirmation}
        onConfirm={handleConfirmExit}
        onCancel={handleCancelExit}
      />

      {/* Shipping Confirmation Dialog */}
      <ShippingConfirmationDialog
        isOpen={showShippingConfirmation}
        onConfirm={handleShippingConfirm}
        onCancel={handleShippingCancel}
      />

      {/* Notes Modal */}
      {showNotesModal && (
        <NotesModalOverlay onClick={() => {
          setShowNotesModal(false);
          setCurrentNotesItemId(null);
          setCurrentNotesValue('');
        }}>
          <NotesModalContent onClick={(e) => e.stopPropagation()}>
            <NotesModalTitle>Product Notes</NotesModalTitle>
            <NotesModalTextarea
              value={currentNotesValue}
              readOnly
              placeholder="No notes available for this product"
            />
            <NotesButtonContainer>
              <CloseNotesButton onClick={() => {
                setShowNotesModal(false);
                setCurrentNotesItemId(null);
                setCurrentNotesValue('');
              }}>
                Close
              </CloseNotesButton>
            </NotesButtonContainer>
          </NotesModalContent>
        </NotesModalOverlay>
      )}
    </Layout>
  );
};

const ErrorMessage = styled.div`
  background-color: #FEE2E2;
  color: #B91C1C;
  padding: 1rem;
  border-radius: 0.375rem;
  margin-bottom: 1.5rem;
  font-weight: 500;
`;

export default CreateQuotePage; 